import React from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout.tsx";
import { Application } from "../../models/Application";
import { notification } from "../../utils/Notifications.tsx";
import {
  fetchApplicationByDate,
  getApplicationsByBranch,
} from "../../services/application.service.ts";
import { Candidate as BranchCandidate } from "../../models/Candidate.ts";
import moment from "moment";
import { getMissingIMSSSalary } from "../../services/candidate.service.ts";
import { checkAllPending } from "../../services/changePosition.service.ts";
import { ChangePosition } from "../../models/ChangePosition.ts";
import ModalContainer from "../../components/ModalContainer.tsx";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import EditSalary from "../../components/colaboradores/EditSalary.tsx";
import {
  setModalAcountant,
  setCurrentUser as setColaboradorUser,
  setShowModal as setShowModalColaborador,
  setModalChildren,
  setModalTitle,
} from "../../store/slices/colaboradoresSlice.ts";
import { Partner } from "../../models/Partner.ts";
import { fetchPartnersIncompleted } from "../../services/partner.service.ts";
import { IncompletedPartner } from "../../models/IncompletedPartner.ts";
import CompletePartner from "../../components/forms/CompletePartner.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import ChangePositionModal from "../../components/colaboradores/ChangePositionModal.tsx";
import { getAllPermissions } from "../../services/permissions.service.ts";
import { getAllIncapacities } from "../../services/incapacities.service.ts";
import { getLoanList } from "../../services/loans.service.ts";
import { can } from "../../auth/can.ts";
import { isBranchConstrained } from "../../auth/sessionAccess.ts";

moment.locale("es");

// ── Status badge ─────────────────────────────────────────────────────────────
const STATUS_COLORS: Record<string, { main: string; light: string }> = {
  Solicitado:               { main: "#2563eb", light: "#dbeafe" },
  Entrevistado:             { main: "#7a6200", light: "#fffbeb" },
  Aceptado:                 { main: "#16a34a", light: "#dcfce7" },
  "Entrevista con Lider":   { main: "#0891b2", light: "#cffafe" },
  "Pre-Colaborador":        { main: "#ea580c", light: "#ffedd5" },
  Rechazado:                { main: "#dc2626", light: "#fee2e2" },
  Cartera:                  { main: "#6b7280", light: "#f3f4f6" },
};

function StatusBadge({ status, label }: { status: string; label?: string }) {
  const colors = STATUS_COLORS[status] ?? { main: "#6b7280", light: "#f3f4f6" };
  return (
    <span style={{
      backgroundColor: colors.light, color: colors.main,
      padding: "0.2rem 0.55rem", borderRadius: "0.375rem",
      fontSize: 12, display: "inline-flex", alignItems: "center", whiteSpace: "nowrap",
    }}>
      {label ?? status}
    </span>
  );
}

// ── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
  .db-page {
    padding: 24px 15px 40px;
    width: 100%;
  }

  .db-greeting {
    margin-bottom: 28px;
    margin-left: 20px;
  }
  .db-greeting__title {
    margin: 0 0 6px;
    font-size: clamp(1.5rem, 3.5vw, 2rem);
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }
  .db-greeting__title span {
    color: #DC2C27;
  }
  .db-greeting__subtitle {
    margin: 0;
    font-size: clamp(0.875rem, 2vw, 0.95rem);
    color: #64748b;
    font-weight: 500;
  }

  .db-stats-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-bottom: 24px;
    width: 100%;
  }

  .db-stats-grid > * {
    flex: 1 1 calc(25% - 11px);
    min-width: 200px;
    max-width: 100%;
  }

  .db-stat-card {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    height: 100%;
    padding: 16px 18px;
    background: #fff;
    border: 1px solid #e8edf2;
    border-radius: 16px;
    cursor: pointer;
    text-align: left;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  }
  .db-stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
    border-color: #d1d9e6;
  }
  .db-stat-card--active {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.1);
  }
  .db-stat-card__icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .db-stat-card__icon i {
    font-size: 18px;
  }
  .db-stat-card__content {
    flex: 1;
    min-width: 0;
  }
  .db-stat-card__count {
    display: block;
    font-size: clamp(1.5rem, 4vw, 1.75rem);
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -0.03em;
    color: #0f172a;
  }
  .db-stat-card__label {
    display: block;
    margin-top: 2px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #64748b;
    line-height: 1.3;
  }

  .db-table { width:100%; border-collapse:collapse; }
  .db-table th {
    position:sticky; top:0; z-index:2;
    background:#f8fafc;
    padding:10px 16px;
    font-size:11.5px; font-weight:700;
    color:#64748b; text-transform:uppercase; letter-spacing:0.05em;
    border-bottom:2px solid #e2e8f0;
    white-space:nowrap;
    cursor:default;
  }
  .db-table td {
    padding:11px 16px;
    font-size:13.5px; color:#374151;
    border-bottom:1px solid #f1f5f9;
    vertical-align:middle;
    white-space:nowrap;
  }
  .db-table tbody tr:hover td { filter: brightness(0.96); }
  .db-table tbody tr:last-child td { border-bottom:none; }

  @keyframes db-shimmer {
    0%   { background-position:-600px 0; }
    100% { background-position: 600px 0; }
  }
  .db-skel {
    display:inline-block; border-radius:8px;
    background:linear-gradient(90deg,#e8edf2 25%,#f4f7fa 50%,#e8edf2 75%);
    background-size:1200px 100%;
    animation:db-shimmer 1.4s infinite linear;
  }

  @media (max-width: 1100px) {
    .db-stats-grid > * {
      flex: 1 1 calc(33.333% - 10px);
      min-width: 180px;
    }
  }
  @media (max-width:768px) {
    .db-page { padding: 18px 12px 32px; }
    .db-greeting { margin-bottom: 20px; }
    .db-stats-grid { gap: 10px; }
    .db-stats-grid > * {
      flex: 1 1 calc(50% - 5px);
      min-width: 150px;
    }
    .db-stat-card { padding: 14px 12px; gap: 10px; }
    .db-stat-card__icon { width: 40px; height: 40px; border-radius: 12px; }
    .db-stat-card__icon i { font-size: 15px; }
    .db-table th, .db-table td { padding:9px 10px; }
  }
  @media (max-width:400px) {
    .db-stats-grid > * {
      flex: 1 1 100%;
      min-width: 0;
    }
  }
`;

// ── Skeleton primitives ──────────────────────────────────────────────────────
function Skel({ w = "100%", h = 14, r = 8, mb = 0 }: { w?: string | number; h?: number; r?: number; mb?: number }) {
  return <div className="db-skel" style={{ width: w, height: h, borderRadius: r, marginBottom: mb }} />;
}

function StatCardSkeleton() {
  return (
    <div className="db-stat-card" style={{ pointerEvents: "none", cursor: "default" }}>
      <Skel w={48} h={48} r={14} />
      <div className="db-stat-card__content">
        <Skel w={40} h={24} r={6} mb={6} />
        <Skel w="75%" h={12} r={6} />
      </div>
    </div>
  );
}

function TableRowSkeleton({ cols = 5 }: { cols?: number }) {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <tr key={i} style={{ opacity: Math.max(0.3, 1 - i * 0.22) }}>
          <td>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Skel w={32} h={32} r={50} />
              <Skel w={110} h={13} />
            </div>
          </td>
          {Array.from({ length: cols - 1 }).map((_, j) => (
            <td key={j}><Skel w={j === cols - 2 ? 76 : "65%"} h={13} /></td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ── Initials avatar ──────────────────────────────────────────────────────────
function InitialsAvatar({ name, color, size = 34 }: { name: string; color: string; size?: number }) {
  const initials = name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `${color}1a`, border: `2px solid ${color}`,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.round(size * 0.36), fontWeight: 700, color,
      flexShrink: 0, letterSpacing: "0.02em", userSelect: "none",
    }}>
      {initials}
    </div>
  );
}

// ── Stat card ────────────────────────────────────────────────────────────────
interface StatCardProps {
  label: string;
  count: number;
  faIcon: string;
  color: string;
  onClick: () => void;
  active: boolean;
}
function StatCard({ label, count, faIcon, color, onClick, active }: StatCardProps) {
  return (
    <button
      type="button"
      className={`db-stat-card${active ? " db-stat-card--active" : ""}`}
      onClick={onClick}
      style={{
        borderColor: active ? color : undefined,
        background: active
          ? `linear-gradient(135deg, #ffffff 0%, ${color}12 100%)`
          : undefined,
        boxShadow: active ? `0 12px 32px ${color}28` : undefined,
      }}
    >
      <div
        className="db-stat-card__icon"
        style={{ background: `${color}18` }}
      >
        <i className={faIcon} style={{ color }} />
      </div>
      <div className="db-stat-card__content">
        <span
          className="db-stat-card__count"
          style={{ color: active ? color : undefined }}
        >
          {count}
        </span>
        <span className="db-stat-card__label">{label}</span>
      </div>
    </button>
  );
}

function DashboardGreeting({ userName }: { userName: string }) {
  return (
    <header className="db-greeting">
      <h1 className="db-greeting__title">
        Hola, <span>{userName}</span>!
      </h1>
    </header>
  );
}

// ── Empty state (inside <tbody>) ──────────────────────────────────────────────
function EmptyRow({ faIcon, text, cols = 5 }: { faIcon: string; text: string; cols?: number }) {
  return (
    <tr>
      <td colSpan={cols}>
        <div style={{ textAlign: "center", padding: "52px 16px" }}>
          <i className={faIcon} style={{ fontSize: 40, color: "#d1d5db", marginBottom: 14, display: "block" }} />
          <p style={{ color: "#9ca3af", margin: 0, fontWeight: 500, fontSize: 14 }}>{text}</p>
        </div>
      </td>
    </tr>
  );
}

// ── Section card ─────────────────────────────────────────────────────────────
function SectionCard({ label, color, faIcon, children }: { label: string; color: string; faIcon: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 14px rgba(0,0,0,.07)", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 22px", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className={faIcon} style={{ color, fontSize: 15 }} />
        </div>
        <span style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{label}</span>
      </div>
      <div style={{ overflow: "auto", minHeight: "calc(100vh - 280px)", maxHeight: "calc(100vh - 280px)" }}>
        {children}
      </div>
    </div>
  );
}

// ── Action button ─────────────────────────────────────────────────────────────
function ActionBtn({ color, onClick, children }: { color: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: color, color: "#fff", border: "none",
        borderRadius: 8, padding: "6px 14px",
        fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentUser, modalAcountant } = useSelector((state: RootState) => state.colaboradores);

  const [applications, setApplications]               = React.useState<Application[]>([]);
  const [noIMSS, setNoIMSS]                           = React.useState<Partner[]>([]);
  const [changePositionPending, setChangePending]     = React.useState<ChangePosition[]>([]);
  const [incompletedPartners, setIncompleted]         = React.useState<IncompletedPartner[]>([]);
  const [permissionsPending, setPermissionsPending]   = React.useState<any[]>([]);
  const [incapacitiesPending, setIncapacitiesPending] = React.useState<any[]>([]);
  const [loansPending, setLoansPending]               = React.useState<any[]>([]);
  const [activeSection, setActiveSection]             = React.useState(0);
  const [open, setOpen]                               = React.useState(false);
  const [currentIncompletedPartner, setCurrentPartner]= React.useState<IncompletedPartner>({} as IncompletedPartner);

  const [loadingApps,     setLoadingApps]     = React.useState(true);
  const [loadingChanges,  setLoadingChanges]  = React.useState(true);
  const [loadingProfiles, setLoadingProfiles] = React.useState(true);
  const [loadingIMSS,     setLoadingIMSS]     = React.useState(true);
  const [loadingPermissions, setLoadingPermissions]   = React.useState(true);
  const [loadingIncapacities, setLoadingIncapacities] = React.useState(true);
  const [loadingLoans, setLoadingLoans]               = React.useState(true);
  const [permissionsQueryFailed, setPermissionsQueryFailed] = React.useState(false);
  const [incapacitiesQueryFailed, setIncapacitiesQueryFailed] = React.useState(false);
  const [loansQueryFailed, setLoansQueryFailed] = React.useState(false);

  const isLider = isBranchConstrained(user);
  const isContador = can("partners.view_imss_salary", user);

  const canViewApplications = can("applications.view", user);
  const canViewPartners = can("partners.view", user);
  const canViewPermissions = can("labor_permissions.view", user);
  const canViewIncapacities = can("incapacities.view", user);
  const canViewLoans = can("loans.view", user);

  React.useEffect(() => {
    if (canViewApplications) {
      fetchApplications();
    } else {
      setLoadingApps(false);
      setLoadingChanges(false);
    }
    if (!isLider && canViewPartners) {
      fetchPendingPartners();
    } else {
      setLoadingProfiles(false);
    }
    if (!isLider && canViewPermissions) {
      fetchPendingPermissions();
    } else {
      setLoadingPermissions(false);
    }
    if (!isLider && canViewIncapacities) {
      fetchPendingIncapacities();
    } else {
      setLoadingIncapacities(false);
    }
    if (!isLider && canViewLoans) {
      fetchPendingLoans();
    } else {
      setLoadingLoans(false);
    }
    if (isContador) {
      fetchNoIMSS();
    } else {
      setLoadingIMSS(false);
    }
  }, []);

  const filterByLeaderBranch = React.useCallback(
    (items: any[]) => {
      if (!isLider || !user.branch) return items;
      const branch = user.branch.toUpperCase();
      return items.filter(
        (item) => item?.partner?.candidate?.branch?.name?.toUpperCase() === branch
      );
    },
    [isLider, user.branch]
  );

  const mapBranchCandidatesToApplications = (candidates: BranchCandidate[]): Application[] =>
    candidates.map((candidate) => ({
      ...candidate.application,
      person: candidate.person,
      vacancy: candidate.vacancy,
      applicationStatus: candidate.application.applicationStatus,
      candidates: [{ ...candidate, recruitmentDate: String(candidate.createdAt) }],
    })) as unknown as Application[];

  const fetchApplications = async () => {
    setLoadingApps(true);
    setLoadingChanges(true);
    try {
      if (isLider && user.branch) {
        const response = (await getApplicationsByBranch(user.branch)) as BranchCandidate[];
        setApplications(mapBranchCandidatesToApplications(response));
      } else {
        const date = new Date().toISOString().split("T")[0];
        const response = (await fetchApplicationByDate(date, "")) as Application[];
        setApplications(response);
      }
    } catch {
      notification("Ocurrió un error", "Error al obtener las solicitudes", "danger");
    } finally {
      setLoadingApps(false);
    }
    if (!isLider) {
      try {
        setChangePending(await checkAllPending(""));
      } catch {
        notification("Error", "Error al obtener cambios de posición", "danger");
      } finally {
        setLoadingChanges(false);
      }
    } else {
      setChangePending([]);
      setLoadingChanges(false);
    }
  };

  const fetchPendingPartners = async () => {
    setLoadingProfiles(true);
    try {
      setIncompleted(await fetchPartnersIncompleted(
        isLider ? `branch=${user.branch}` : ""
      ));
    } catch {
      notification("", "Error al obtener los colaboradores", "danger");
    } finally {
      setLoadingProfiles(false);
    }
  };

  const fetchNoIMSS = async () => {
    setLoadingIMSS(true);
    try { setNoIMSS(await getMissingIMSSSalary()); }
    catch { notification("Ocurrió un error", "Error al obtener sin IMSS", "danger"); }
    finally { setLoadingIMSS(false); }
  };

  const fetchPendingPermissions = async () => {
    setLoadingPermissions(true);
    try {
      const response = await getAllPermissions({
        limit: 200,
        offset: 0,
        sort: "id",
        order: "DESC",
        status: "pending",
      });
      const data = response?.data || [];
      const pending = data.filter((p: any) => !p?.approved && !p?.isRejected);
      setPermissionsQueryFailed(false);
      setPermissionsPending(filterByLeaderBranch(pending));
    } catch (error: any) {
      const status = error?.response?.status ?? error?.status;
      if (status !== 403) {
        notification("Error", "Error al obtener permisos pendientes", "danger");
        setPermissionsPending([]);
      }
      setPermissionsQueryFailed(status === 403);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const fetchPendingIncapacities = async () => {
    setLoadingIncapacities(true);
    try {
      const response = await getAllIncapacities({
        limit: 200,
        offset: 0,
        sort: "id",
        order: "DESC",
        status: "pending",
      });
      const data = response?.data || [];
      const pending = data.filter((i: any) => !i?.approved && !i?.isRejected);
      setIncapacitiesQueryFailed(false);
      setIncapacitiesPending(filterByLeaderBranch(pending));
    } catch (error: any) {
      const status = error?.response?.status ?? error?.status;
      if (status !== 403) {
        notification("Error", "Error al obtener incapacidades pendientes", "danger");
        setIncapacitiesPending([]);
      }
      setIncapacitiesQueryFailed(status === 403);
    } finally {
      setLoadingIncapacities(false);
    }
  };

  const fetchPendingLoans = async () => {
    setLoadingLoans(true);
    try {
      const response = await getLoanList({
        limit: 200,
        offset: 0,
        sort: "id",
        order: "DESC",
      });
      const data = response?.data || [];
      const pending = data.filter((l: any) => !l?.approved && !l?.isRejected);
      setLoansQueryFailed(false);
      setLoansPending(filterByLeaderBranch(pending));
    } catch (error: any) {
      const status = error?.response?.status ?? error?.status;
      if (status !== 403) {
        notification("Error", "Error al obtener préstamos pendientes", "danger");
        setLoansPending([]);
      }
      setLoansQueryFailed(status === 403);
    } finally {
      setLoadingLoans(false);
    }
  };

  function handleClick(application) {
    if (["Solicitado", "Entrevistado"].includes(application.applicationStatus.name)) {
      navigate("/result/form", { state: { ...application } });
    } else {
      navigate("/solicitudes/aprobar/" + application.id);
    }
  }

  const isAdmin =
    can("applications.view", user) ||
    can("partners.view", user) ||
    can("labor_permissions.view", user);

  const sectionLoading = [
    loadingApps,
    loadingChanges,
    loadingProfiles,
    loadingIMSS,
    loadingPermissions,
    loadingIncapacities,
    loadingLoans,
  ][activeSection] ?? false;

  const statCards = [
    { label: "Solicitudes", count: applications.length, faIcon: "fa-solid fa-clipboard-list", color: "#50B7C0", section: 0, loading: loadingApps, show: true },
    { label: "Cambios de posición", count: changePositionPending.length, faIcon: "fa-solid fa-arrows-rotate", color: "#f59e0b", section: 1, loading: loadingChanges, show: !isLider },
    { label: "Perfiles incompletos", count: incompletedPartners.length, faIcon: "fa-solid fa-user-pen", color: "#8b5cf6", section: 2, loading: loadingProfiles, show: !isLider && canViewPartners },
    { label: "Sin IMSS", count: noIMSS.length, faIcon: "fa-solid fa-notes-medical", color: "#DC2C27", section: 3, loading: loadingIMSS, show: isContador },
    { label: "Permisos", count: permissionsPending.length, faIcon: "fa-solid fa-calendar-check", color: "#ec4899", section: 4, loading: loadingPermissions, show: !isLider && canViewPermissions },
    { label: "Incapacidades", count: incapacitiesPending.length, faIcon: "fa-solid fa-heart-pulse", color: "#38bdf8", section: 5, loading: loadingIncapacities, show: !isLider && canViewIncapacities },
    { label: "Préstamos", count: loansPending.length, faIcon: "fa-solid fa-hand-holding-dollar", color: "#22c55e", section: 6, loading: loadingLoans, show: !isLider && canViewLoans },
  ].filter((c) => c.show);

  const activeCard = statCards.find((c) => c.section === activeSection);
  const sectionTitle = activeCard?.label ?? "Pendientes";
  const sectionFaIcon = activeCard?.faIcon ?? "fa-solid fa-list";
  const accentColor = activeCard?.color ?? "#DC2C27";

  const greetingName = React.useMemo(() => {
    const first = toCamelCase(String(user?.name || "").trim());
    const last = toCamelCase(
      String(
        (user as { lastName?: string; last_name?: string })?.lastName ??
          (user as { last_name?: string })?.last_name ??
          ""
      ).trim()
    );
    return [first, last].filter(Boolean).join(" ") || "Usuario";
  }, [user?.name, (user as { lastName?: string; last_name?: string })?.lastName, (user as { last_name?: string })?.last_name]);

  return (
    <React.Fragment>
      <style>{CSS}</style>

      <Layout>
        <div className="db-page">

          {isAdmin && (
            <>
              <DashboardGreeting userName={greetingName} />

              <div className="db-stats-grid">
                {statCards.map((card) =>
                  card.loading ? (
                    <StatCardSkeleton key={card.section} />
                  ) : (
                    <StatCard
                      key={card.section}
                      label={card.label}
                      count={card.count}
                      faIcon={card.faIcon}
                      color={card.color}
                      active={activeSection === card.section}
                      onClick={() => setActiveSection(card.section)}
                    />
                  )
                )}
              </div>

              {/* Content table */}
              <SectionCard
                label={sectionTitle}
                color={accentColor}
                faIcon={sectionFaIcon}
              >

                {/* ── Solicitudes ── */}
                {activeSection === 0 && (
                  <table className="db-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Status</th>
                        <th>Fecha</th>
                        <th>Puesto</th>
                        <th style={{ textAlign: "right" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionLoading && <TableRowSkeleton cols={5} />}
                      {!sectionLoading && applications.length === 0 && (
                        <EmptyRow faIcon="fa-solid fa-clipboard-list" text="No hay solicitudes pendientes" cols={5} />
                      )}
                      {!sectionLoading && applications.map((app, i) => {
                        const statusName  = app.applicationStatus.name;
                        const statusLabel = statusName === "Solicitado" ? "Candidato" : statusName;
                        const fullName    = `${toCamelCase(app.person.firstName)} ${toCamelCase(app.person.lastName)} ${toCamelCase(app.person.secondLastName)}`;
                        const fecha       = app.candidates[0]?.recruitmentDate
                          ? moment(app.candidates[0].recruitmentDate).format("D MMM YYYY")
                          : moment(`${app.applicationDate} ${app.applicationTime}`).format("D MMM YYYY");
                        const puesto      = app.candidates[0]?.recruitmentDate
                          ? app.candidates[0]?.vacancy?.position?.name
                          : app.vacancy?.position?.name;
                        return (
                          <tr key={i} style={{ backgroundColor: i % 2 !== 0 ? `${accentColor}0d` : "transparent" }}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <InitialsAvatar name={fullName} color={accentColor} />
                                <span style={{ fontWeight: 600, color: "#0f172a" }}>{fullName}</span>
                              </div>
                            </td>
                            <td><StatusBadge status={statusName} label={statusLabel} /></td>
                            <td style={{ color: "#64748b" }}>{fecha}</td>
                            <td style={{ color: "#64748b" }}>{puesto}</td>
                            <td style={{ textAlign: "right" }}>
                              <ActionBtn color={accentColor} onClick={() => handleClick(app)}>
                                Revisar
                              </ActionBtn>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* ── Cambios de posición ── */}
                {activeSection === 1 && (
                  <table className="db-table">
                    <thead>
                      <tr>
                        <th>Colaborador</th>
                        <th>Nuevo puesto</th>
                        <th>Solicitado por</th>
                        <th>Fecha</th>
                        <th style={{ textAlign: "right" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionLoading && <TableRowSkeleton cols={5} />}
                      {!sectionLoading && changePositionPending.length === 0 && (
                        <EmptyRow faIcon="fa-solid fa-arrows-rotate" text="No hay cambios de posición pendientes" cols={5} />
                      )}
                      {!sectionLoading && changePositionPending.map((cp, i) => {
                        const partnerName   = toCamelCase(`${cp.partner.person.firstName} ${cp.partner.person.lastName} ${cp.partner.person.secondLastName}`);
                        const requesterName = toCamelCase(`${cp.requesterUser.name} ${cp.requesterUser.lastName} ${cp.requesterUser.secondLastName}`);
                        const fecha         = cp.requestDate ? moment(cp.requestDate).format("D MMM YYYY") : "—";
                        return (
                          <tr key={i} style={{ backgroundColor: i % 2 !== 0 ? `${accentColor}0d` : "transparent" }}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <InitialsAvatar name={partnerName} color={accentColor} />
                                <span style={{ fontWeight: 600, color: "#0f172a" }}>{partnerName}</span>
                              </div>
                            </td>
                            <td>
                              <span style={{ background: `${accentColor}15`, color: accentColor, borderRadius: 6, padding: "2px 8px", fontSize: 12.5, fontWeight: 600 }}>
                                {cp.newPosition.name}
                              </span>
                            </td>
                            <td style={{ color: "#64748b" }}>{requesterName}</td>
                            <td style={{ color: "#64748b" }}>{fecha}</td>
                            <td style={{ textAlign: "right" }}>
                              <ActionBtn color={accentColor} onClick={() => {
                                navigate("colaboradores");
                                dispatch(setShowModalColaborador(true));
                                dispatch(setModalChildren(
                                  <ChangePositionModal
                                    closeFn={() => dispatch(setShowModalColaborador(false))}
                                    partner={cp.partner as any}
                                  />
                                ));
                                dispatch(setModalTitle(`Cambiar de puesto a ${partnerName}`));
                              }}>
                                Revisar
                              </ActionBtn>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* ── Perfiles incompletos ── */}
                {activeSection === 2 && (
                  <table className="db-table">
                    <thead>
                      <tr>
                        <th>Colaborador</th>
                        <th>Posición</th>
                        <th style={{ textAlign: "right" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionLoading && <TableRowSkeleton cols={3} />}
                      {!sectionLoading && incompletedPartners.length === 0 && (
                        <EmptyRow faIcon="fa-solid fa-user-pen" text="No hay colaboradores incompletos" cols={3} />
                      )}
                      {!sectionLoading && incompletedPartners.map((partner, i) => {
                        const fullName = toCamelCase(`${partner.person.firstName} ${partner.person.lastName} ${partner.person.secondLastName}`);
                        const puesto   = partner?.candidate?.vacancy?.position?.name ?? "Sin puesto";
                        return (
                          <tr key={i} style={{ backgroundColor: i % 2 !== 0 ? `${accentColor}0d` : "transparent" }}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <InitialsAvatar name={fullName} color={accentColor} />
                                <span style={{ fontWeight: 600, color: "#0f172a" }}>{fullName}</span>
                              </div>
                            </td>
                            <td style={{ color: "#64748b" }}>{puesto}</td>
                            <td style={{ textAlign: "right" }}>
                              <ActionBtn color={accentColor} onClick={() => { setCurrentPartner(partner); setOpen(true); }}>
                                Completar
                              </ActionBtn>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* ── Sin IMSS ── */}
                {activeSection === 3 && (
                  <table className="db-table">
                    <thead>
                      <tr>
                        <th>Colaborador</th>
                        <th>Fecha ingreso</th>
                        <th style={{ textAlign: "right" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionLoading && <TableRowSkeleton cols={3} />}
                      {!sectionLoading && noIMSS.length === 0 && (
                        <EmptyRow faIcon="fa-solid fa-notes-medical" text="No hay colaboradores sin salario de IMSS asignado" cols={3} />
                      )}
                      {!sectionLoading && noIMSS.map((partner, i) => {
                        const fullName = toCamelCase(`${partner.person.firstName} ${partner.person.lastName} ${partner.person.secondLastName}`);
                        const fecha    = partner?.candidate?.createdAt ? moment(partner.candidate.createdAt).format("D MMM YYYY") : "—";
                        return (
                          <tr key={i} style={{ backgroundColor: i % 2 !== 0 ? `${accentColor}0d` : "transparent" }}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <InitialsAvatar name={fullName} color={accentColor} />
                                <span style={{ fontWeight: 600, color: "#0f172a" }}>{fullName}</span>
                              </div>
                            </td>
                            <td style={{ color: "#64748b" }}>{fecha}</td>
                            <td style={{ textAlign: "right" }}>
                              <ActionBtn color={accentColor} onClick={() => {
                                dispatch(setColaboradorUser(partner.id));
                                dispatch(setModalAcountant(true));
                              }}>
                                Editar salario
                              </ActionBtn>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* ── Permisos ── */}
                {activeSection === 4 && (
                  <table className="db-table">
                    <thead>
                      <tr>
                        <th>Colaborador</th>
                        <th>Fecha permiso</th>
                        <th>Días</th>
                        <th>Estado</th>
                        <th style={{ textAlign: "right" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionLoading && <TableRowSkeleton cols={5} />}
                      {!sectionLoading && permissionsQueryFailed && (
                        <EmptyRow faIcon="fa-solid fa-calendar-check" text="No se pudieron consultar los permisos" cols={5} />
                      )}
                      {!sectionLoading && !permissionsQueryFailed && permissionsPending.length === 0 && (
                        <EmptyRow faIcon="fa-solid fa-calendar-check" text="No hay permisos pendientes" cols={5} />
                      )}
                      {!sectionLoading && permissionsPending.map((permission, i) => {
                        const fullName = toCamelCase(`${permission?.partner?.person?.firstName || ""} ${permission?.partner?.person?.lastName || ""} ${permission?.partner?.person?.secondLastName || ""}`);
                        return (
                          <tr key={permission.id} style={{ backgroundColor: i % 2 !== 0 ? `${accentColor}0d` : "transparent" }}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <InitialsAvatar name={fullName} color={accentColor} />
                                <span style={{ fontWeight: 600, color: "#0f172a" }}>{fullName || "-"}</span>
                              </div>
                            </td>
                            <td style={{ color: "#64748b" }}>
                              {permission?.startRequested ? moment.unix(permission.startRequested).format("D MMM YYYY") : "-"}
                            </td>
                            <td style={{ color: "#64748b" }}>{permission?.daysRequested ?? "-"}</td>
                            <td><StatusBadge status={permission?.approved ? "Aprobado" : permission?.isRejected ? "Rechazado" : "Solicitado"} label={permission?.approved ? "Aprobado" : permission?.isRejected ? "Rechazado" : "Pendiente"} /></td>
                            <td style={{ textAlign: "right" }}>
                              <ActionBtn color={accentColor} onClick={() => navigate(`/colaboradores/aprobar-permiso/${permission.id}`)}>
                                Revisar
                              </ActionBtn>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* ── Incapacidades ── */}
                {activeSection === 5 && (
                  <table className="db-table">
                    <thead>
                      <tr>
                        <th>Colaborador</th>
                        <th>Rango</th>
                        <th>Días</th>
                        <th>Estado</th>
                        <th style={{ textAlign: "right" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionLoading && <TableRowSkeleton cols={5} />}
                      {!sectionLoading && incapacitiesQueryFailed && (
                        <EmptyRow faIcon="fa-solid fa-heart-pulse" text="No se pudieron consultar las incapacidades" cols={5} />
                      )}
                      {!sectionLoading && !incapacitiesQueryFailed && incapacitiesPending.length === 0 && (
                        <EmptyRow faIcon="fa-solid fa-heart-pulse" text="No hay incapacidades pendientes" cols={5} />
                      )}
                      {!sectionLoading && incapacitiesPending.map((incapacity, i) => {
                        const fullName = toCamelCase(`${incapacity?.partner?.person?.firstName || ""} ${incapacity?.partner?.person?.lastName || ""} ${incapacity?.partner?.person?.secondLastName || ""}`);
                        const start = incapacity?.startDate ? moment.unix(incapacity.startDate).format("D MMM YYYY") : "-";
                        const end = incapacity?.endDate ? moment.unix(incapacity.endDate).format("D MMM YYYY") : "-";
                        const days = incapacity?.startDate && incapacity?.endDate
                          ? moment.unix(incapacity.endDate).diff(moment.unix(incapacity.startDate), "days") + 1
                          : "-";
                        return (
                          <tr key={incapacity.id} style={{ backgroundColor: i % 2 !== 0 ? `${accentColor}0d` : "transparent" }}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <InitialsAvatar name={fullName} color={accentColor} />
                                <span style={{ fontWeight: 600, color: "#0f172a" }}>{fullName || "-"}</span>
                              </div>
                            </td>
                            <td style={{ color: "#64748b" }}>{`${start} - ${end}`}</td>
                            <td style={{ color: "#64748b" }}>{days}</td>
                            <td><StatusBadge status="Solicitado" label="Pendiente" /></td>
                            <td style={{ textAlign: "right" }}>
                              <ActionBtn color={accentColor} onClick={() => navigate(`/colaboradores/aprobar-incapacidad/${incapacity.id}`)}>
                                Revisar
                              </ActionBtn>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* ── Prestamos ── */}
                {activeSection === 6 && (
                  <table className="db-table">
                    <thead>
                      <tr>
                        <th>Colaborador</th>
                        <th>Monto solicitado</th>
                        <th>Monto por quincena</th>
                        <th>Estado</th>
                        <th style={{ textAlign: "right" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectionLoading && <TableRowSkeleton cols={5} />}
                      {!sectionLoading && loansQueryFailed && (
                        <EmptyRow faIcon="fa-solid fa-hand-holding-dollar" text="No se pudieron consultar los préstamos" cols={5} />
                      )}
                      {!sectionLoading && !loansQueryFailed && loansPending.length === 0 && (
                        <EmptyRow faIcon="fa-solid fa-hand-holding-dollar" text="No hay préstamos pendientes" cols={5} />
                      )}
                      {!sectionLoading && loansPending.map((loan, i) => {
                        const fullName = toCamelCase(`${loan?.partner?.person?.firstName || ""} ${loan?.partner?.person?.lastName || ""} ${loan?.partner?.person?.secondLastName || ""}`);
                        return (
                          <tr key={loan.id} style={{ backgroundColor: i % 2 !== 0 ? `${accentColor}0d` : "transparent" }}>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <InitialsAvatar name={fullName} color={accentColor} />
                                <span style={{ fontWeight: 600, color: "#0f172a" }}>{fullName || "-"}</span>
                              </div>
                            </td>
                            <td style={{ color: "#64748b" }}>${Number(loan?.requestAmount || 0).toLocaleString("es-MX")}</td>
                            <td style={{ color: "#64748b" }}>${Number(loan?.amountDiscount || 0).toLocaleString("es-MX")}</td>
                            <td><StatusBadge status="Solicitado" label="Pendiente" /></td>
                            <td style={{ textAlign: "right" }}>
                              <ActionBtn color={accentColor} onClick={() => navigate(`/prestamos/editar/${loan.id}`)}>
                                Revisar
                              </ActionBtn>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

              </SectionCard>
            </>
          )}
        </div>
      </Layout>

      <ModalContainer
        title={`Completar información de ${toCamelCase(currentIncompletedPartner?.person?.firstName)} ${toCamelCase(currentIncompletedPartner?.person?.lastName)} ${toCamelCase(currentIncompletedPartner?.person?.secondLastName)}`}
        open={open}
        closeFn={() => { setOpen(false); setCurrentPartner({} as IncompletedPartner); }}
        children={
          <CompletePartner
            partner={currentIncompletedPartner}
            close={() => { setOpen(false); setCurrentPartner({} as IncompletedPartner); }}
            refetch={() => fetchPendingPartners()}
          />
        }
        size="lg"
      />

      <ModalContainer
        open={modalAcountant}
        closeFn={() => { dispatch(setModalAcountant(false)); }}
        title="Editar salario"
        children={
          <EditSalary
            colaboradorId={currentUser}
            closeFn={() => { dispatch(setModalAcountant(false)); }}
            refetch={async () => { dispatch(setModalAcountant(false)); await fetchNoIMSS(); }}
          />
        }
      />
    </React.Fragment>
  );
};

export default AdminDashboard;
